import { useCallback, useEffect, useRef, useState } from "react";
import { IDashboardContext, useDashboardContext } from "../contexts/dashboard-context";
import { useSelectedZone } from "./useSelection";
import { useInteractiveMessage } from "./useInteractiveMessage";
import { useEditor } from "./useEditor";
import { useErrorHandling } from "./useErrorHandling";
import { useGoogleAnalytics } from "./useGoogleAnalytics";
import { useSelectedColorTweaks } from "../components/bottom-photo-menus/photo-editing-panel/use-selected-colortweaks";
import { useMessageToState } from "./useMessageToState";

enum GaActions {
	brightness = "BrightnessChanged",
	contrast = "ContrastChanged",
	saturation = "SaturationChanged",
}

type FilterProperty = keyof typeof GaActions;

function getUIValue(
	usedColorTweaks: eXpress.core.ColorTweaks,
	property: FilterProperty,
	dashboard: IDashboardContext,
	steps: number,
): number {
	const fullRangeValue = usedColorTweaks[property];
	const limitedRangeValue =
		fullRangeValue / dashboard.getColorTweakConversionFactor(property, fullRangeValue);
	const uiValue = Math.round(limitedRangeValue * steps);

	return uiValue;
}

export function usePhotoFilters() {
	const steps = 100;

	const selectedZone = useSelectedZone();
	const usedColorTweaks = useSelectedColorTweaks(selectedZone);
	const dashboard = useDashboardContext();
	const googleAnalytics = useGoogleAnalytics();
	const { commandManager } = useEditor();
	const { createErrorHandler } = useErrorHandling();

	const { markInteractiveMessage, makeNonInteractive, makeNonInteractiveDebounced } =
		useInteractiveMessage(commandManager);

	// 1. Combine slider states into a single object
	const [uiValues, setUiValues] = useState<Record<FilterProperty, number>>(() => ({
		brightness: getUIValue(usedColorTweaks, "brightness", dashboard, steps),
		contrast: getUIValue(usedColorTweaks, "contrast", dashboard, steps),
		saturation: getUIValue(usedColorTweaks, "saturation", dashboard, steps),
	}));

	// 2. Track previous values for GA events per property
	const prevValues = useRef<Record<FilterProperty, number | undefined>>({
		brightness: undefined,
		contrast: undefined,
		saturation: undefined,
	});

	const getCanApplyPhotoFilters = useCallback(() => {
		return !!selectedZone && dashboard.canApplyColorTweaks(selectedZone);
	}, [dashboard, selectedZone]);
	const canApplyPhotoFilters = useMessageToState({
		name: "use-photo-filters",
		getState: getCanApplyPhotoFilters,
	});

	// 3. Centralized change handler logic
	const handleValueChange = useCallback(
		(property: FilterProperty, newUiValue: number, logGaEvent = true) => {
			newUiValue = Math.min(Math.max(newUiValue, -steps), steps);

			setUiValues((prev) => ({ ...prev, [property]: newUiValue }));

			if (selectedZone && dashboard.canApplyColorTweaks(selectedZone)) {
				const newLimitedRangeValue = newUiValue / steps;

				const newFullRangeValue =
					newLimitedRangeValue *
					dashboard.getColorTweakConversionFactor(property, newLimitedRangeValue);

				dashboard.applyColorTweaks(selectedZone, { [property]: newFullRangeValue }, true);

				markInteractiveMessage();

				makeNonInteractiveDebounced()
					.then(() => {
						if (logGaEvent && newUiValue !== prevValues.current[property]) {
							googleAnalytics.trackEventToGa({
								trackingAction: GaActions[property],
							});
						}
						prevValues.current[property] = newUiValue;
					})
					.catch(createErrorHandler());
			}
		},
		[
			createErrorHandler,
			dashboard,
			selectedZone,
			makeNonInteractiveDebounced,
			markInteractiveMessage,
			googleAnalytics,
			steps,
		],
	);

	// 4. Create stable, individual callbacks for each property
	const onBrightnessChange = useCallback(
		(newUiValue: number, logGaEvent?: boolean) =>
			handleValueChange("brightness", newUiValue, logGaEvent),
		[handleValueChange],
	);

	const onContrastChange = useCallback(
		(newUiValue: number, logGaEvent?: boolean) =>
			handleValueChange("contrast", newUiValue, logGaEvent),
		[handleValueChange],
	);

	const onSaturationChange = useCallback(
		(newUiValue: number, logGaEvent?: boolean) =>
			handleValueChange("saturation", newUiValue, logGaEvent),
		[handleValueChange],
	);

	// 5. Keep UI values synchronized with external changes
	useEffect(() => {
		setUiValues({
			brightness: getUIValue(usedColorTweaks, "brightness", dashboard, steps),
			contrast: getUIValue(usedColorTweaks, "contrast", dashboard, steps),
			saturation: getUIValue(usedColorTweaks, "saturation", dashboard, steps),
		});
	}, [usedColorTweaks, dashboard, steps]);

	// 6. Cleanup on unmount
	useEffect(
		() => () => {
			makeNonInteractive();
		},
		[makeNonInteractive],
	);

	return {
		brightness: {
			currentUISliderValue: uiValues.brightness,
			onChange: onBrightnessChange,
		},
		contrast: {
			currentUISliderValue: uiValues.contrast,
			onChange: onContrastChange,
		},
		saturation: {
			currentUISliderValue: uiValues.saturation,
			onChange: onSaturationChange,
		},
		canApplyPhotoFilters,
	};
}
